import React, { useState, useEffect, Suspense, lazy } from 'react'
import ClientLayout from './components/client/ClientLayout'
import HomePage from './components/client/HomePage'
import HowItWorksPage from './components/client/HowItWorksPage'
import BenefitsPage from './components/client/BenefitsPage'
import HelpPage from './components/client/HelpPage'
import ClubRewards from './components/ClubRewards'
import ClubHistory from './components/ClubHistory'
import ClubProfile from './components/ClubProfile'
import ClientAccess from './components/ClientAccess'
import OperatorLogin from './components/OperatorLogin'
import OperatorDashboard from './components/OperatorDashboard'
import ClientRegistration from './components/ClientRegistration'
import OnboardingFlow from './components/OnboardingFlow'
import ControlMantenimiento from './components/ControlMantenimiento'
import { auth, db, EMULATORS_REQUIRED } from './firebase'
import { doc, getDoc, onSnapshot, updateDoc } from 'firebase/firestore'
import { onAuthStateChanged, signOut } from 'firebase/auth'
import ClubDashboard from './components/ClubDashboard'
import { trackEvento, EVENTOS } from './utils/analytics'

const AdminDashboard = lazy(() => import('./components/AdminDashboard'))

// Logo del Club — se usa en la pantalla de mantenimiento y en la de error de emuladores
const LogoMaja = ({ className = 'w-20 h-20' }) => (
  <img src="/logo192.png" alt="Club Maja" className={className} onError={e => { e.target.style.display = 'none' }} />
)

/**
 * Estados de autenticación posibles:
 *   LOADING                    — verificando sesión inicial
 *   UNAUTHENTICATED            — sin usuario
 *   AUTHENTICATED_PROFILE_INCOMPLETE — Firebase Auth OK, pero usuarios/{uid} no existe o está incompleto
 *   AUTHENTICATED              — usuario completo y con rol cliente
 */
const AUTH_STATUS = {
  LOADING:                      'loading',
  UNAUTHENTICATED:              'unauthenticated',
  AUTHENTICATED_PROFILE_INCOMPLETE: 'profile_incomplete',
  AUTHENTICATED:                'authenticated',
}

const App = () => {
  // ─── Ruta /control-mantenimiento — completamente fuera del flujo normal ───
  // Se evalúa antes que cualquier gate (mantenimiento, auth, emuladores).
  // No muestra ninguna pantalla del Club ni permite otras operaciones.
  if (window.location.pathname === '/control-mantenimiento') {
    return <ControlMantenimiento />
  }

  const [currentView, setCurrentView]         = useState('home')
  const [loggedInUser, setLoggedInUser]       = useState(null)
  const [userProfile, setUserProfile]         = useState(null)
  const [authStatus, setAuthStatus]           = useState(AUTH_STATUS.LOADING)
  const [googleUser, setGoogleUser]           = useState(null)  // usuario Firebase Auth (solo durante onboarding)
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)
  const [successMessage, setSuccessMessage]   = useState('')
  const [localOperador, setLocalOperador]     = useState('')
  const [siteConfig, setSiteConfig]           = useState(null)
  const [estadoClub, setEstadoClub]           = useState(null)   // null = cargando
  const [emuladoresError, setEmuladoresError] = useState(false)

  // ─── Verificar emuladores en modo local ────────────────────────────────────
  useEffect(() => {
    if (!EMULATORS_REQUIRED) return
    // Ping al emulador de Firestore. Si no responde, mostrar error y bloquear.
    fetch('http://localhost:8080/')
      .then(() => setEmuladoresError(false))
      .catch(() => setEmuladoresError(true))
  }, [])

  // ─── Suscripción al estado del Club ───────────────────────────────────────
  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'configuracion_publica', 'club'),
      (snap) => {
        if (snap.exists()) {
          setEstadoClub(snap.data())
        } else {
          // Doc no existe → tratar como activo (compatibilidad)
          setEstadoClub({ estado: 'activo', mensaje: '' })
        }
      },
      () => {
        // Error de lectura (offline, etc.) → tratar como activo para no bloquear indefinidamente
        setEstadoClub({ estado: 'activo', mensaje: '' })
      }
    )
    return () => unsub()
  }, [])

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const snap = await getDoc(doc(db, 'configuracion_admin', 'global'))
        if (snap.exists()) setSiteConfig(snap.data())
      } catch {}
    }
    loadConfig()
  }, [])

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setAuthStatus(AUTH_STATUS.UNAUTHENTICATED)
        setLoggedInUser(null)
        setUserProfile(null)
        setGoogleUser(null)
        return
      }

      try {
        const userSnap = await getDoc(doc(db, 'usuarios', user.uid))

        // onboardingCompletado === false (strict): doc existe pero onboarding pendiente
        // undefined (usuarios históricos sin el campo) NO se trata como false → no se bloquean
        if (!userSnap.exists() || userSnap.data().onboardingCompletado === false) {
          setGoogleUser(user)
          setAuthStatus(AUTH_STATUS.AUTHENTICATED_PROFILE_INCOMPLETE)
          trackEvento(EVENTOS.PROFILE_INCOMPLETE_DETECTED)
          return
        }

        const profile = { uid: user.uid, ...userSnap.data() }

        if (profile.rol === 'cliente') {
          // Sincronizar authProviders en Firestore si cambiaron
          const currentProviders = user.providerData.map(p => p.providerId)
          const storedProviders  = profile.authProviders || []
          const providersChanged = currentProviders.some(p => !storedProviders.includes(p))
          if (providersChanged) {
            try {
              await updateDoc(doc(db, 'usuarios', user.uid), { authProviders: currentProviders })
            } catch {}
          }

          setUserProfile({ ...profile, authProviders: currentProviders })
          setLoggedInUser({ uid: user.uid, rol: profile.rol })
          setGoogleUser(null)
          setAuthStatus(AUTH_STATUS.AUTHENTICATED)
          return
        }

        if (profile.rol === 'operador' || profile.rol === 'admin') {
          setUserProfile(profile)
          setLoggedInUser({ uid: user.uid, rol: profile.rol })
          setAuthStatus(AUTH_STATUS.AUTHENTICATED)
          if (profile.rol === 'operador') {
            setLocalOperador((prev) => prev || profile.local || '')
            setCurrentView('operatorDashboard')
          } else {
            setCurrentView('adminDashboard')
          }
          return
        }

        // rol desconocido — tratar como no autenticado
        setAuthStatus(AUTH_STATUS.UNAUTHENTICATED)
      } catch {
        setAuthStatus(AUTH_STATUS.UNAUTHENTICATED)
      }
    })
    return () => unsub()
  }, [])

  const refreshUserProfile = async () => {
    try {
      const user = auth.currentUser
      if (user) {
        const snap = await getDoc(doc(db, 'usuarios', user.uid))
        if (snap.exists()) setUserProfile({ uid: user.uid, ...snap.data() })
      }
    } catch {}
  }

  const handleLogin = async (targetView, userType = 'client', localSeleccionado = '') => {
    try {
      const user = auth.currentUser
      if (!user) { mostrarToast('Usuario no autenticado.'); return }

      const snap = await getDoc(doc(db, 'usuarios', user.uid))
      if (!snap.exists() || snap.data().onboardingCompletado === false) {
        // Sin perfil o onboarding pendiente → onboarding (strict false; undefined no bloquea)
        setGoogleUser(user)
        setAuthStatus(AUTH_STATUS.AUTHENTICATED_PROFILE_INCOMPLETE)
        return
      }

      const profile = { uid: user.uid, ...snap.data() }
      setUserProfile(profile)
      setLoggedInUser({ uid: user.uid, rol: profile.rol })

      if (userType === 'operator' && profile.rol === 'operador') {
        setLocalOperador(localSeleccionado || '')
        setCurrentView('operatorDashboard')
      } else if (userType === 'admin' && profile.rol === 'admin') {
        setCurrentView('adminDashboard')
      } else if (userType === 'client' && profile.rol === 'cliente') {
        setCurrentView(targetView || 'home')
      } else {
        mostrarToast('Acceso denegado. No tenés permisos para este panel.')
      }
    } catch {
      mostrarToast('Error obteniendo datos del usuario.')
    }
  }

  const handleLogout = async () => {
    try { await signOut(auth) } catch {}
    setLoggedInUser(null)
    setUserProfile(null)
    setGoogleUser(null)
    setLocalOperador('')
    setAuthStatus(AUTH_STATUS.UNAUTHENTICATED)
    setCurrentView('home')
  }

  const handleRegistrationSuccess = () => {
    mostrarToast('¡Registro exitoso! Ahora podés acceder con tu email y contraseña.')
    setTimeout(() => setCurrentView('login'), 3000)
  }

  // Callback cuando GoogleOnboarding termina de crear el perfil
  const handleOnboardingComplete = async () => {
    await refreshUserProfile()
    const user = auth.currentUser
    if (user) {
      const snap = await getDoc(doc(db, 'usuarios', user.uid))
      if (snap.exists()) {
        const profile = { uid: user.uid, ...snap.data() }
        setUserProfile(profile)
        setLoggedInUser({ uid: user.uid, rol: profile.rol })
        setGoogleUser(null)
        setAuthStatus(AUTH_STATUS.AUTHENTICATED)
        setCurrentView('home')
      }
    }
  }

  const mostrarToast = (msg) => {
    setSuccessMessage(msg)
    setShowSuccessMessage(true)
    setTimeout(() => setShowSuccessMessage(false), 3000)
  }

  // ─── Error: emuladores no disponibles en modo local ──────────────────────
  if (EMULATORS_REQUIRED && emuladoresError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-6">
        <div className="text-center max-w-sm">
          <LogoMaja className="w-16 h-16 mx-auto mb-6 opacity-30" />
          <p className="text-base font-semibold text-gray-800 mb-2">Emuladores no disponibles</p>
          <p className="text-sm text-gray-500 mb-4">
            En modo local la aplicación require los emuladores de Firebase.<br />
            Ejecutá <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">firebase emulators:start</code> y recargá.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="text-sm text-maja-red font-semibold"
          >
            Reintentar
          </button>
        </div>
      </div>
    )
  }

  // ─── Loading (estado del Club aún no llegó O auth aún verificando) ────────
  if (authStatus === AUTH_STATUS.LOADING || estadoClub === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-maja-red border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-sm text-maja-ink/40 font-medium">Cargando...</p>
        </div>
      </div>
    )
  }

  // ─── Modo mantenimiento ────────────────────────────────────────────────────
  if (estadoClub?.estado === 'mantenimiento') {
    const _email = auth.currentUser?.email ?? null
    const _enTestEmails = _email && Array.isArray(estadoClub?.testEmails) && estadoClub.testEmails.includes(_email)
    const _esAdmin = loggedInUser?.rol === 'admin' || userProfile?.rol === 'admin'
    const _esLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    const _autorizado = _enTestEmails || _esAdmin || _esLocalhost

    if (!_autorizado) {
      const _textoMantenimiento = (
        <>
          <p className="text-lg font-bold text-gray-900 leading-snug">
            ¡Estamos mejorando Club Maja!
          </p>
          <p className="text-sm text-gray-500 leading-relaxed">
            Trabajamos para volver muy pronto con una mejor experiencia y muchos más beneficios para vos.
          </p>
          <p className="text-sm text-gray-500 leading-relaxed pt-1">
            ¡Gracias por acompañarnos!
          </p>
        </>
      )

      if (!_email) {
        // Sin sesión → solo mensaje de mantenimiento, sin login
        return (
          <div className="min-h-screen flex flex-col items-center justify-center bg-white px-6">
            <LogoMaja className="w-24 h-24 mb-10" />
            <div className="text-center max-w-xs space-y-3">
              {_textoMantenimiento}
            </div>
          </div>
        )
      }

      // Tiene sesión pero email no autorizado → bloqueo con opción de cerrar sesión
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-white px-6">
          <LogoMaja className="w-24 h-24 mb-10" />
          <div className="text-center max-w-xs space-y-3">
            {_textoMantenimiento}
          </div>
          <button
            onClick={() => signOut(auth)}
            className="mt-8 text-xs text-gray-400 underline underline-offset-2 hover:text-gray-600"
          >
            Cerrar sesión
          </button>
        </div>
      )
    }
    // Email autorizado → continuar render normal
  }

  // ─── Onboarding Google — perfil incompleto ────────────────────────────────
  if (authStatus === AUTH_STATUS.AUTHENTICATED_PROFILE_INCOMPLETE) {
    return (
      <div className="min-h-screen bg-white">
        <ClientLayout
          currentView="login"
          setCurrentView={() => {}}
          userProfile={null}
          loggedInUser={null}
          onLogout={handleLogout}
          siteConfig={siteConfig}
        >
          <OnboardingFlow
            googleUser={googleUser}
            siteConfig={siteConfig}
            onComplete={handleOnboardingComplete}
          />
        </ClientLayout>
      </div>
    )
  }

  // ─── Panel operador → redirigir al CRM ───────────────────────────────────
  if (loggedInUser?.rol === 'operador') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-6 text-center gap-6">
        <img src="/brand/logo-oficial.png" alt="Maja Morena" className="h-16 w-16 object-contain" />
        <div>
          <h1 className="text-2xl font-black text-gray-800">Panel de operador</h1>
          <p className="text-gray-500 mt-2 max-w-xs">
            El panel del local se maneja desde el CRM de Maja Morena.
          </p>
        </div>
        <a
          href="https://maja-morena-ops.web.app"
          className="h-12 px-8 rounded-full bg-maja-red text-white font-black text-sm inline-flex items-center gap-2"
        >
          Ir al panel del local →
        </a>
        <button onClick={handleLogout} className="text-sm text-gray-400 hover:text-gray-600 font-extrabold">
          Cerrar sesión
        </button>
      </div>
    )
  }

  if (loggedInUser?.rol === 'admin') {
    return (
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center font-black text-gray-400">Cargando panel...</div>}>
        <AdminDashboard onLogout={handleLogout} />
      </Suspense>
    )
  }


  // ─── Vista cliente ────────────────────────────────────────────────────────
  const isCliente = loggedInUser?.rol === 'cliente' && !!userProfile

  const renderClientView = () => {
    switch (currentView) {
      case 'howItWorks':
        return <HowItWorksPage setCurrentView={setCurrentView} siteConfig={siteConfig} userProfile={userProfile} />
      case 'benefits':
        return <BenefitsPage setCurrentView={setCurrentView} siteConfig={siteConfig} />
      case 'canjes':
        return (
          <ClubRewards
            userPoints={userProfile?.puntos ?? 0}
            refreshUserProfile={refreshUserProfile}
            setCurrentView={setCurrentView}
            siteConfig={siteConfig}
            isLoggedIn={isCliente}
          />
        )
      case 'branches':
        return isCliente
          ? <ClubDashboard userProfile={userProfile} setCurrentView={setCurrentView} siteConfig={siteConfig} refreshUserProfile={refreshUserProfile} />
          : <HomePage setCurrentView={setCurrentView} siteConfig={siteConfig} userProfile={userProfile} />
      case 'help':
        return <HelpPage setCurrentView={setCurrentView} siteConfig={siteConfig} />
      case 'operatorLogin':
        // OperatorLogin maneja el login de personal (operador → CRM, admin → AdminDashboard)
        return <OperatorLogin onLogin={handleLogin} onBack={() => setCurrentView('home')} />
      case 'login':
        return isCliente
          ? <ClubDashboard userProfile={userProfile} setCurrentView={setCurrentView} siteConfig={siteConfig} refreshUserProfile={refreshUserProfile} />
          : <ClientAccess onLogin={handleLogin} setCurrentView={setCurrentView} siteConfig={siteConfig} />
      case 'registration':
        return isCliente
          ? <ClubDashboard userProfile={userProfile} setCurrentView={setCurrentView} siteConfig={siteConfig} refreshUserProfile={refreshUserProfile} />
          : <ClientRegistration onRegisterSuccess={handleRegistrationSuccess} siteConfig={siteConfig} setCurrentView={setCurrentView} />
      case 'history':
        return userProfile
          ? <ClubHistory userId={userProfile.uid} siteConfig={siteConfig} setCurrentView={setCurrentView} userPoints={userProfile?.puntos ?? 0} userProfile={userProfile} />
          : <ClientAccess onLogin={handleLogin} setCurrentView={setCurrentView} siteConfig={siteConfig} />
      case 'profile':
        return userProfile
          ? (
            <ClubProfile
              userPoints={userProfile?.puntos ?? 0}
              userName={userProfile?.nombre || ''}
              userEmail={userProfile?.email || ''}
              userLastName={userProfile?.apellido || ''}
              userDni={userProfile?.dni || ''}
              userTelefono={userProfile?.telefono || ''}
              userNacimiento={userProfile?.nacimiento || ''}
              userDomicilio={userProfile?.domicilio || ''}
              siteConfig={siteConfig}
              setCurrentView={setCurrentView}
            />
          )
          : <ClientAccess onLogin={handleLogin} setCurrentView={setCurrentView} siteConfig={siteConfig} />
      case 'home':
      default:
        return isCliente
          ? <ClubDashboard userProfile={userProfile} setCurrentView={setCurrentView} siteConfig={siteConfig} refreshUserProfile={refreshUserProfile} />
          : <HomePage setCurrentView={setCurrentView} siteConfig={siteConfig} userProfile={userProfile} />
    }
  }

  const layoutProps = {
    currentView,
    setCurrentView,
    userProfile,
    loggedInUser,
    onLogout: handleLogout,
    siteConfig,
  }

  return (
    <div className="min-h-screen bg-white">
      <ClientLayout {...layoutProps}>
        {renderClientView()}
      </ClientLayout>
      {showSuccessMessage && (
        <div className="fixed top-4 right-4 z-50">
          <div className="bg-maja-ink text-white px-6 py-4 rounded-2xl shadow-soft">
            <p className="font-semibold">Listo</p>
            <p className="text-sm text-white/80">{successMessage}</p>
          </div>
        </div>
      )}
    </div>
  )
}

export default App
